// /src/utils/onboardingStorage.js

import axios from 'axios';

const STORAGE_KEY = 'membersOnboardingCompleted';

/** Dispatched on `window` when onboarding is marked complete (secondary nav unlock). */
export const ONBOARDING_COMPLETE_EVENT = 'members-onboarding-completed';

/**
 * Toggle superadmin onboarding bypass (no UI — edit here).
 * `true` → superadmins skip onboarding gates; `false` → same rules as other users.
 */
export const SUPERADMIN_ONBOARDING_BYPASS = true;

/** RBAC SecGroup.groupName for guests — skip org-membership block + member onboarding. */
export const GUEST_GROUP_NAME = 'Guest';

export function superAdminOnboardingBypassActive(isSuperAdmin) {
  return SUPERADMIN_ONBOARDING_BYPASS === true && Boolean(isSuperAdmin);
}

/** Users in the Guest RBAC group skip org membership gate and member onboarding. */
export function guestAccessBypassActive(isGuest) {
  return Boolean(isGuest);
}

/** Combined bypass: superadmin toggle and/or Guest group. */
export function siteAccessBypassActive({ isSuperAdmin, isGuest } = {}) {
  return (
    superAdminOnboardingBypassActive(isSuperAdmin)
    || guestAccessBypassActive(isGuest)
  );
}

export function isOnboardingComplete() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** True when API returned a completion timestamp (viewer's row, not view-as subject). */
export function isOnboardingCompleteFromServer(onboardingCompletedAt) {
  return onboardingCompletedAt != null && onboardingCompletedAt !== '';
}

/** @deprecated Prefer completeMemberOnboardingOnServer — kept for legacy local cache sync. */
export function setOnboardingCompleteLocal(onboardingCompletedAt) {
  try {
    window.localStorage.setItem(STORAGE_KEY, '1');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(ONBOARDING_COMPLETE_EVENT, {
          detail: { onboardingCompletedAt: onboardingCompletedAt ?? new Date().toISOString() },
        }),
      );
    }
  } catch {
    // quota / private mode
  }
}

/**
 * Persist onboarding completion on the server.
 * @param {{ useViewAsSubject?: boolean }} options — when true, completes the view-as member (header kept).
 */
export async function completeMemberOnboardingOnServer(options = {}) {
  const { useViewAsSubject = false } = options;
  const res = await axios.post(
    '/api/user/onboarding/complete',
    {},
    {
      withCredentials: true,
      skipViewAs: !useViewAsSubject,
    },
  );
  const completedAt = res.data?.onboardingCompletedAt ?? new Date().toISOString();
  setOnboardingCompleteLocal(completedAt);
  return res.data;
}

/** @deprecated Use completeMemberOnboardingOnServer */
export function setOnboardingComplete() {
  setOnboardingCompleteLocal();
}

/** Identity check fields required before member website navigation is unlocked. */
export function isIdentityCheckComplete({ email, discordName, handles, gcuAccepted }) {
  return Boolean(email && discordName && handles?.length > 0 && gcuAccepted === true);
}

/**
 * Secondary navbar + full member site access (non–super-admin bypass).
 * Identity check must pass; onboarding must be completed unless superadmin/guest bypass is active.
 * @param {boolean} [onboardingCompleteOverride] — server-backed status when provided
 */
export function canShowSecondaryNavBar(
  { email, discordName, handles, gcuAccepted },
  isSuperAdmin,
  onboardingCompleteOverride,
  orgMembershipEligible = true,
  isGuest = false,
) {
  if (!isIdentityCheckComplete({ email, discordName, handles, gcuAccepted })) {
    return false;
  }
  if (
    !orgMembershipEligible
    && !siteAccessBypassActive({ isSuperAdmin, isGuest })
  ) {
    return false;
  }
  if (siteAccessBypassActive({ isSuperAdmin, isGuest })) {
    return true;
  }
  if (typeof onboardingCompleteOverride === 'boolean') {
    return onboardingCompleteOverride;
  }
  return isOnboardingComplete();
}

/**
 * After login / bootstrap: identity check → onboarding → saved deep link or dashboard.
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @param {{ email?: string|null, gcuAccepted?: boolean, discordName?: string|null, handles?: unknown[]|null, isSuperAdmin?: boolean, isGuest?: boolean, orgMembershipEligible?: boolean }} auth
 */
export function navigateAfterAuthenticated(navigate, auth) {
  if (typeof navigate !== 'function') return;
  const {
    email,
    gcuAccepted,
    discordName,
    handles,
    isSuperAdmin,
    isGuest,
    orgMembershipEligible,
  } = auth || {};
  if (!isIdentityCheckComplete({ email, discordName, handles, gcuAccepted })) {
    navigate('/GamerIdentityCheck', { replace: true });
    return;
  }
  const bypass = siteAccessBypassActive({ isSuperAdmin, isGuest });
  // Org membership is enforced by PrivateRoute / GamerIdentityCheck once status loads.
  // When the caller already knows the user is not eligible, stay on identity check.
  if (orgMembershipEligible === false && !bypass) {
    navigate('/GamerIdentityCheck', { replace: true });
    return;
  }
  if (!isOnboardingComplete() && !bypass) {
    navigate('/members/onboarding', { replace: true });
    return;
  }
  // Note: post-login redirect still uses local cache; view-as / server state uses useWebsiteAccessGate.
  const initialPath =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('initialPath') : null;
  if (initialPath) {
    sessionStorage.removeItem('initialPath');
    navigate(initialPath, { replace: true });
  } else {
    navigate('/members/personalhub', { replace: true });
  }
}
